import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import OrderContainerService from '../../../../../services/orders/orderContainer';
import GeneralService from '../../../../../services/general';
import OrderGoodsService from '../../../../../services/orders/orderGoods';
import OrderService from '../../../../../services/orders/order';

// props
import { IGoodsResourceShortProps } from '../../../../../props/goods/goods';
import { IOrderResourceProps } from '../../../../../props/orders/order';

// components
import { Checkbox, DefaultButton, Dropdown, IconButton, Link, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TagPicker, TextField, Toggle } from '@fluentui/react';
import Text from '../../../../typography/text';
import Label from '../../../../typography/label';
import GoodsService from '../../../../../services/goods/goods';
import ErrorService from '../../../../../services/general/error';
import GoodsForm from '../../../../goods/general/form';
import { PPNPercentageOptions, PPhPercentageOptions } from '../../../../../manifests/taxPercentage';
import PricingService from '../../../../../services/general/pricing';
import { IOrderGoodsResourceProps } from '../../../../../props/orders/orderGoods';
import PermissionsService from '../../../../../services/permissions';
import Details from '../../../../typography/details';

interface IOrderGoodsFormProps {
    orderId: string;
    orderGoodsId?: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    goods?: IGoodsResourceShortProps;
    quantityAmount: string;
    notes: string;
    pricingMethod: string;
    ppn: string;
    pph: string;
    includePPn?: boolean;
    includePPh?: boolean;
    ppnPercentage: string;
    pphPercentage: string;
    price: string;
    finalPrice: string;
    nettPrice: string;
    invoicePrice: string;
}

type FormDataErrorProps = {
    goods?: string;
    quantityAmount?: string;
    notes?: string;
    price?: string;
    ppn?: string;
    pph?: string;
}

const OrderGoodsForm: React.FC<IOrderGoodsFormProps> = (props: IOrderGoodsFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [updatePrice, setUpdatePrice] = React.useState<boolean>(false);
    const [orderGoods, setOrderGoods] = React.useState<IOrderGoodsResourceProps | undefined>();
    const [order, setOrder] = React.useState<IOrderResourceProps | undefined>();
    const [data, setData] = React.useState<FormDataProps>({
        quantityAmount: "",
        notes: "",
        pricingMethod: "perquantity",
        price: "",
        ppn: "0",
        pph: "0",
        ppnPercentage: "0",
        pphPercentage: "0",
        finalPrice: "0",
        nettPrice: "0",
        invoicePrice: "0"
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const mode: 'create' | 'update' = props.orderGoodsId === undefined ? 'create' : 'update';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            const _order = await OrderService.get(props.orderId);
            setOrder(_order);
        } catch (e) {
            console.error(e);
        }

        if (props.orderGoodsId) {
            const _orderGoods = await OrderGoodsService.get(props.orderId, props.orderGoodsId);

            setData({
                goods: _orderGoods.goods,
                quantityAmount: _orderGoods.quantityAmount != undefined ? Number(_orderGoods.quantityAmount) + "" : "0",
                notes: _orderGoods.notes || "",
                price: _orderGoods.price != undefined ? Number(_orderGoods.price) + "" : "0",
                finalPrice: _orderGoods.totalPrice != undefined ? Number(_orderGoods.totalPrice) + "" : "0",
                pricingMethod: _orderGoods.pricingMethod || "perquantity",
                ppn: _orderGoods.ppn != undefined ? Number(_orderGoods.ppn) + "" : "0",
                pph: _orderGoods.pph != undefined ? Number(_orderGoods.pph) + "" : "0",
                ppnPercentage: _orderGoods.ppnPercentage != undefined ? Number(_orderGoods.ppnPercentage) + "" : "0",
                pphPercentage: _orderGoods.pphPercentage != undefined ? Number(_orderGoods.pphPercentage) + "" : "0",
                nettPrice: _orderGoods.nettPrice != undefined ? Number(_orderGoods.nettPrice) + "" : "0",
                invoicePrice: _orderGoods.invoicePrice != undefined ? Number(_orderGoods.invoicePrice) + "" : "0",
                includePPn: _orderGoods.includePPn || false,
                includePPh: _orderGoods.includePPh || false
            });
            setOrderGoods(_orderGoods);
        }

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.goods || error.notes || error.quantityAmount) {
            return true;
        } else if (!data.goods || data.quantityAmount === "") {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { goods, price, notes, quantityAmount, pricingMethod, ppn, pph, includePPn, includePPh, ppnPercentage, pphPercentage, finalPrice, nettPrice, invoicePrice } = data;

            // create form data
            let fd = new FormData();
            fd.append("goodsId", goods?.id || "");
            fd.append("price", (price && !isNaN(Number(price))) ? price : '0');
            fd.append("notes", notes || "");
            fd.append("quantityAmount", quantityAmount || "0");
            fd.append("pricingMethod", pricingMethod || "perquantity");

            if (updatePrice || Number(quantityAmount) !== orderGoods?.quantityAmount) {
                fd.append("totalPrice", (finalPrice && !isNaN(Number(finalPrice))) ? finalPrice : '0');
                fd.append("finalPrice", (finalPrice && !isNaN(Number(finalPrice))) ? finalPrice : '0');
                fd.append("nettPrice", (nettPrice && !isNaN(Number(nettPrice))) ? nettPrice : '0');
                fd.append("invoicePrice", (invoicePrice && !isNaN(Number(invoicePrice))) ? invoicePrice : '0');
                fd.append("price", (price && !isNaN(Number(price))) ? price : '0');
                fd.append("ppn", (ppn && !isNaN(Number(ppn))) ? ppn : '0');
                fd.append("pph", (pph && !isNaN(Number(pph))) ? pph : '0');
                fd.append("includePPn", (includePPn ?? false) + "");
                fd.append("includePPh", (includePPh ?? false) + "");
                fd.append("ppnPercentage", (ppnPercentage && !isNaN(Number(ppnPercentage))) ? ppnPercentage : '0');
                fd.append("pphPercentage", (pphPercentage && !isNaN(Number(pphPercentage))) ? pphPercentage : '0');

                if (orderGoods?.invoiceId) {
                    fd.append("updateInvoice", "1");
                }
            }

            if (props.orderGoodsId === undefined) {
                await OrderGoodsService.create(props.orderId, fd);
            } else {
                await OrderGoodsService.update(props.orderId, props.orderGoodsId, fd);
            }

            banner.add({
                key: mode + '_order_goods_success',
                variant: 'success',
                icon: faCheck,
                text: `Goods "${goods?.name}" ${mode === 'create' ? 'created' : 'updated'} successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
            console.error('Error submitting order goods:', e);
            banner.add({
                key: mode + '_order_goods_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to ${mode} goods: ` + ErrorService.getMessage(e)
            });
        }
    }

    const _onSearchGoods = async (keyword: string) => {
        try {
            const qs: string[] = [];
            qs.push(`status=current`);
            if (keyword && keyword.trim() !== '') { qs.push(`search=${keyword}`) }
            if (keyword && keyword.trim() !== '') { qs.push(`search=${keyword}`) }

            const data = (await GoodsService.retrieve.get(qs.join("&"))).map((item) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));

            return data;
        } catch (error) {
            banner.add({
                key: "search_goods_error",
                text: "Failed to search related goods. Error: " + ErrorService.getMessage(error),
                variant: 'error',
                icon: faXmarkCircle
            });

            return [];
        }
    }

    const _onGoodsCreated = (refresh?: boolean, goods?: IGoodsResourceShortProps) => {
        if (goods) {
            _onGoodsChanged(goods);
        }

        setActiveSurface(undefined);
    }

    const _onGoodsCreatedAnother = (goods: IGoodsResourceShortProps) => {
        _onGoodsChanged(goods);
    }

    const _onGoodsChanged = (item?: IGoodsResourceShortProps) => {
        data.goods = item;
        data.price = data.price || "0";
        data.quantityAmount = Number(item?.quantityAmount || 0) + "";

        setData({ ...data });
    }

    const calculateAllPrice = (_data: FormDataProps) => {
        const pricing = PricingService.order.calculate(_data, _data.pricingMethod);
        setData({ ..._data, ...pricing });
    }

    return <Panel headerText={mode === 'create' ? 'Create Order Goods' : 'Update Order Goods'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating goods ..." : "Updating goods ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <Stack>
                        <Label size={'small'}>Goods</Label>
                        <TagPicker removeButtonAriaLabel='Remove'
                            selectedItems={data.goods ? [{
                                key: data.goods.id,
                                name: data.goods.name,
                                metadata: data.goods
                            }] as any : []}
                            itemLimit={1}
                            onItemSelected={(tag: any) => {
                                const value = tag ? tag.metadata : undefined;
                                _onGoodsChanged(value);
                                return null;
                            }}
                            onChange={(tags) => {
                                if (tags && tags.length < 1) {
                                    data.goods = undefined;
                                    setData({ ...data });
                                }
                            }}
                            onResolveSuggestions={_onSearchGoods}
                            onEmptyResolveSuggestions={() => _onSearchGoods('')}
                            disabled={submitting} />
                        <Text size={'small'}>Cannot find goods? <Link onClick={() => { setActiveSurface('goods.create') }}>Click here</Link> to create goods.</Text>
                    </Stack>
                    <Stack.Item>
                        <TextField label={"Notes"}
                            value={data.notes}
                            multiline
                            resizable={false}
                            autoAdjustHeight
                            rows={4}
                            onChange={(evt, value) => {
                                data.notes = value || "";

                                const validation = ValidationService.combination(value, ['limit'], { maxChars: 1000 });
                                error.notes = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            suffix={data.goods?.notes || ""}
                            errorMessage={error.notes}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Quantity"}
                            value={data.quantityAmount}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.quantityAmount = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.quantityAmount = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            suffix={data.goods?.quantityUnit || ""}
                            errorMessage={error.quantityAmount}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack className={'divider'}></Stack>
                    {/*<Checkbox label={'Update price'}
                        checked={updatePrice}
                        disabled={(orderGoods?.invoiceId || "").trim() !== "" && !PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions)}
                        onChange={(ev, checked) => setUpdatePrice(checked || false)} />
                    {orderGoods?.invoiceId && updatePrice ? <Text className={'color-yellow'}>!!! WARNING !!! This goods already associated with an invoice, updating this additional goods' price will also update invoice amount.</Text> : null}
                    <Stack.Item>
                        <Dropdown label={"Pricing Method"}
                            selectedKey={data.pricingMethod}
                            options={[
                                { key: 'lumpsum', text: 'Lump sum' },
                                { key: 'perquantity', text: 'Per quantity' }
                            ]}
                            onChange={(evt, opt) => {
                                data.pricingMethod = opt?.key as string;
                                setData({ ...data });
                            }}
                            disabled={submitting || !updatePrice} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={data.pricingMethod === 'perquantity' ? "Price per Quantity" : "Total Price"}
                            value={data.price}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.price = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.price = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            suffix={data.price !== "" ? GeneralService.convertNumberToWords(Number(data.price)) : undefined}
                            errorMessage={error.price}
                            disabled={submitting || !updatePrice} />
                    </Stack.Item>
                    {data.pricingMethod === 'perquantity' ? <Stack>
                        <Label size={'small'}>Total Price</Label>
                        <Text>Rp. {GeneralService.getNumberWithSeparator(Number(data.price) * Number(data.quantityAmount))}</Text>
                    </Stack> : null}
                    <Stack tokens={{ childrenGap: 20 }} horizontal>
                        <Stack.Item>
                            <Checkbox checked={data.includePPn}
                                label='Include PPn'
                                onChange={(ev, checked) => {
                                    data.includePPn = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }}
                                disabled={submitting || !updatePrice} />
                        </Stack.Item>
                        <Stack.Item>
                            <Checkbox checked={data.includePPh}
                                label='Include PPh'
                                onChange={(ev, checked) => {
                                    data.includePPh = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }}
                                disabled={submitting || !updatePrice} />
                        </Stack.Item>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPn</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPNPercentageOptions}
                                    selectedKey={data.ppnPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.ppnPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    styles={{ root: { width: 100 } }}
                                    disabled={submitting || !updatePrice} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.ppn}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.ppn = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.ppn = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.ppn}
                                    disabled={submitting || data.ppnPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPh</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPhPercentageOptions}
                                    selectedKey={data.pphPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.pph = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.pphPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    styles={{ root: { width: 100 } }}
                                    disabled={submitting || !updatePrice} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.pph}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.pph = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.pph = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.pph}
                                    disabled={submitting || data.pphPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Total price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.finalPrice)}`} style={{width: '50%'}} />
                        <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.invoicePrice)}`} style={{width: '50%'}} />
                    </Stack>*/}
                </> : null
            }
        </Stack>
        {activeSurface === 'goods.create' ? <GoodsForm onDismissed={_onGoodsCreated} onGoodsCreated={_onGoodsCreatedAnother} defaultCompany={order?.company} defaultCustomer={order?.customer} /> : null}
    </Panel>
};

export default OrderGoodsForm;
